import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Filters } from 'hooks/store/service-orders';
import { PageHeaderWithCheckboxesContext } from 'components/ui/page-header-with-checkboxes';
import { Form, FormField } from 'components/ui/form';
import { InputGs, DatePickerGs, DateRangePickerGs } from 'components/ui/Forms';
import { Button as ButtonGs } from 'components/ui/ButtonGs';

import { formSchema, type FormSchema } from './form-schema';
import { createFilterParams } from './utils';
import { addYears } from 'date-fns';

type Props = {
  onSubmitFilters: (filters: Filters) => void;
  onResetFilters: () => void;
  onStatusesChange: (statuses: string[]) => void;
};

export const ServiceOrdersFilter = ({
  onResetFilters,
  onSubmitFilters,
  onStatusesChange,
}: Props) => {
  const {
    selectedCheckboxValues,
    resetSelectedCheckboxValues,
  } = React.useContext(PageHeaderWithCheckboxesContext);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const resetForm = React.useCallback(() => {
    form.reset({
      material: '',
      invoiceNumber: '',
      createdAt: undefined,
      startDate: undefined,
      endDate: undefined,
    }, { keepValues: false });
    onResetFilters();
    resetSelectedCheckboxValues();
  }, [form, onResetFilters, resetSelectedCheckboxValues]);

  const onFormSubmit = React.useCallback((formData: FormSchema) => {
    const filters = createFilterParams({
      formData,
      currentStatus: selectedCheckboxValues,
    });
    onSubmitFilters(filters);
  }, [selectedCheckboxValues, onSubmitFilters]);

  React.useEffect(() => {
    onStatusesChange(selectedCheckboxValues);
  }, [onStatusesChange, selectedCheckboxValues]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onFormSubmit)} className={'w-full'}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <FormField
            control={form.control}
            name={'material'}
            render={({ field }) => (
              <InputGs {...field} type={'text'} label={'Código do produto'} />
            )}
          />
          <FormField
            control={form.control}
            name={'invoiceNumber'}
            render={({ field }) => (
              <InputGs {...field} type={'text'} label={'Número da NF'} />
            )}
          />
          <FormField
            control={form.control}
            name={'createdAt'}
            render={({ field }) => (
              <DateRangePickerGs
                selected={{
                  from: field.value?.from,
                  to: field.value?.to,
                }}
                onSelect={field.onChange}
                label={'Período de criação'}
                clearable
              />
            )}
          />
          <FormField
            control={form.control}
            name={'startDate'}
            render={({ field }) => (
              <DatePickerGs
                {...field}
                label={'Iniciado em'}
                fieldValue={field.value ? new Date(field.value) : undefined}
                fieldOnChange={field.onChange}
                maxDate={addYears(new Date(), 100)}
              />
            )}
          />
          <FormField
            control={form.control}
            name={'endDate'}
            render={({ field }) => (
              <DatePickerGs
                {...field}
                label={'Finalizado em'}
                fieldValue={field.value ? new Date(field.value) : undefined}
                fieldOnChange={field.onChange}
                maxDate={addYears(new Date(), 100)}
              />
            )}
          />
        </div>
        <div className={'flex gap-4 mt-6'}>
          <ButtonGs.Root
            type="button"
            variant="secondary"
            size="sm"
            onClick={resetForm}
          >
            Limpar filtros
          </ButtonGs.Root>
          <ButtonGs.Root type="submit" size="sm">
            Filtrar
          </ButtonGs.Root>
        </div>
      </form>
    </Form>
  );
};